import { LocaleAnchor } from '@/i18n/router';
import { formError } from '@/i18n/form-error';
import { smsDisclosure } from '../../supabase/functions/_shared/locale';
import { useLocale } from '@/i18n/LocaleProvider';
import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useLocalizedToast as useToast } from '@/i18n/shared';
import { supabase } from '@/integrations/supabase/client';
import TurnstileWidget, { TurnstileWidgetHandle } from '@/components/TurnstileWidget';
import { MessageSquareText, CheckCircle2 } from 'lucide-react';

// Express-consent copy structured after Twilio's model opt-in form (checkbox
// text naming the message types + frequency, then explicit Message Frequency /
// Standard Rates / Help & Stop blocks). Keep in sync with CONSENT_TEXT in
// supabase/functions/subscribe-sms/index.ts — the server stores its copy with
// each row as the TCPA proof-of-consent record.

// Client-side sanity check only — the edge function normalizes to E.164 and
// is the real validator. Accepts "(555) 123-4567", "555-123-4567", "+44 …".
const looksLikePhone = (raw: string) => {
  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) return digits.length >= 8 && digits.length <= 15;
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

interface SmsOptInFormProps {
  /** Which CTA/surface sent the visitor here — recorded with the opt-in. */
  source?: string;
}

const SmsOptInForm = ({ source }: SmsOptInFormProps) => {
  const { locale, formatLocale } = useLocale();
  const disclosure = smsDisclosure[locale];
  const [phone, setPhone] = useState('');
  const [consented, setConsented] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  // Honeypot: hidden field humans never see; form-spam bots fill every input.
  const [website, setWebsite] = useState('');
  const renderedAt = useRef(Date.now());
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Bot check: honeypot filled or submitted faster than a human could type.
    // Pretend it worked so the bot doesn't adapt.
    const formMs = Date.now() - renderedAt.current;
    if (website || formMs < 3000) {
      setIsSubscribed(true);
      return;
    }

    if (!looksLikePhone(phone)) {
      toast({
        title: 'Check your number',
        description: 'Enter a valid mobile number, e.g. (555) 123-4567 or +44 7911 123456.',
        variant: 'destructive',
      });
      return;
    }

    if (!consented) {
      toast({
        title: 'Consent required',
        description: 'Please check the box to agree to receive text messages.',
        variant: 'destructive',
      });
      return;
    }

    if (!turnstileToken) {
      toast({
        title: 'One moment…',
        description: "Still verifying you're human — please try again in a second.",
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.functions.invoke('subscribe-sms', {
        body: { locale,
          phone: phone.trim(),
          source: source || undefined,
          website: website || undefined,
          form_ms: formMs,
          turnstile_token: turnstileToken,
        },
      });

      if (error) {
        console.error('Function error details:', error);
        if (error.context?.body) {
          const errorBody = await error.context.json?.() || error.context.body;
          console.error('Error response body:', errorBody);
          throw Object.assign(error, { code: errorBody?.code || errorBody?.error });
        }
        throw error;
      }

      setIsSubscribed(true);
    } catch (error: unknown) {
      console.error('SMS opt-in error:', error);
      const message = await formError(error, locale);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      // Turnstile tokens are single-use — mint a fresh one for any retry.
      turnstileRef.current?.reset();
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return <Localize messages={sharedPortuguese}>{(
      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        <CheckCircle2 className="h-10 w-10 text-gizmo-600 dark:text-gizmo-400" />
        <h3 className="text-xl font-semibold text-foreground">You're on the list!</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          We've recorded your opt-in for GizmoData SMS alerts. Message frequency varies, and
          message &amp; data rates may apply. Reply <strong>STOP</strong> at any time to
          unsubscribe, or <strong>HELP</strong> for help.
        </p>
      </div>
    )}</Localize>;
  }

  return <Localize messages={sharedPortuguese}>{(
    <form onSubmit={handleSubmit} className="relative space-y-4">
      <div>
        <label htmlFor="sms-phone" className="mb-1.5 block text-sm font-medium text-foreground">
          Mobile phone number *
        </label>
        <Input
          id="sms-phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          disabled={isLoading}
          maxLength={20}
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          US numbers can skip the +1; include a country code (e.g. +44) for international numbers.
        </p>
      </div>
      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <Checkbox
          id="sms-consent"
          checked={consented}
          onCheckedChange={(checked) => setConsented(checked === true)}
          disabled={isLoading}
          className="mt-0.5"
        />
        <label htmlFor="sms-consent" className="cursor-pointer text-sm leading-relaxed text-muted-foreground">
          {disclosure.checkbox}
        </label>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        <p><strong className="text-foreground">{disclosure.frequencyLabel}</strong>{' '}{disclosure.frequency}</p>
        <p><strong className="text-foreground">{disclosure.ratesLabel}</strong>{' '}{disclosure.rates}</p>
        <p><strong className="text-foreground">{disclosure.helpLabel}</strong>{' '}{disclosure.help}</p>
        <p>
          <LocaleAnchor href="/terms-of-service" className="underline hover:text-foreground">Terms of Service</LocaleAnchor>
          {' | '}
          <LocaleAnchor href="/privacy-policy" className="underline hover:text-foreground">Privacy Policy</LocaleAnchor>
        </p>
      </div>
      <TurnstileWidget ref={turnstileRef} onToken={setTurnstileToken} />
      <Button
        type="submit"
        disabled={isLoading}
        data-ga-cta="sms_optin_submit"
        data-ga-location="sms_alerts_page"
        className="w-full bg-gizmo-600 font-medium text-white shadow-md hover:bg-gizmo-700 dark:bg-gizmo-500 dark:hover:bg-gizmo-600"
      >
        <MessageSquareText className="mr-2 h-4 w-4" />
        {isLoading ? 'Subscribing…' : 'Yes, sign me up!'}
      </Button>
      {/* Honeypot — visually removed but present in the DOM so bots fill it. */}
      <div className="absolute -left-[9999px] top-0 h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="sms-website">Website</label>
        <input
          id="sms-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
    </form>
  )}</Localize>;
};

export default SmsOptInForm;
